<template>
  <div class="gender-step">
    <div class="gender-selection-content">
      <!-- Title -->
      <div class="title-section">
        <h2 class="step-title h1">{{ step.title }}</h2>
        <p v-if="step.subtitle" class="step-subtitle body">{{ step.subtitle }}</p>
      </div>

      <!-- Gender Options -->
      <div class="gender-options">
        <button
          class="gender-option full-width button"
          :class="{ selected: selectedGender === 'male' }"
          @click="selectGender('male')"
        >
          <span class="button">Мужской</span>
        </button>

        <button
          class="gender-option full-width button"
          :class="{ selected: selectedGender === 'female' }"
          @click="selectGender('female')"
        >
          <span class="button">Женский</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useTasteClarificationModal } from '@/features/taste/store'
import type { TasteStep, GenderValue } from '@/features/taste/types/taste-config'

interface Props {
  step: TasteStep & { type: 'gender' }
}

const props = defineProps<Props>()
const tasteStore = useTasteClarificationModal()
const selectedGender = ref<GenderValue | null>(null)

function selectGender(gender: GenderValue) {
  selectedGender.value = gender
  tasteStore.updateTasteData('gender', gender)
}

onMounted(() => {
  selectedGender.value = tasteStore.tasteModal.tasteData.gender || null
})
</script>

<style scoped>
.gender-step {
  display: flex;
  padding: var(--Padding-XXL, 40px) 120px;
  flex-direction: column;
  align-items: center;
  gap: var(--Spacing-XL, 40px);
  align-self: stretch;
}

.gender-selection-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--Spacing-XL, 40px);
  align-self: stretch;
}

.title-section {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: var(--Spacing-S, 8px);
  align-self: stretch;
}

.step-title {
  color:var(--primary-blue-100);
  margin: 0;
}

.step-subtitle {
  color:var(--primary-blue-80);
  margin: 0;
}

.gender-options {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--Spacing-S, 12px);
  align-self: stretch;
}

.gender-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 80px;
  padding: 32px 24px;
  border: 2px solid #e5e5e7;
  border-radius: 16px;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
  color:var(--active-100);
}

.gender-option:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 122, 255, 0.15);
}

.gender-option.selected {
  color: var(--white-100);
  background: var(--active-100);
  box-shadow: 0 4px 12px rgba(0, 122, 255, 0.2);
}

@media (min-width: 768px) and (max-width: 1023px) {
  .gender-step {
    padding: 0 20px 20px;
    gap: 40px;
  }

  .gender-selection-content {
    gap: 40px;
    padding-top: 30px;
  }

  .title-section {
    align-items: flex-start;
    text-align: left;
    gap: 8px;
  }

  .step-title {
    font-size: 32px;
    line-height: 1.09;
    font-weight: 700;
  }

  .step-subtitle {
    font-size: 15px;
    line-height: 1.3;
  }

  .gender-options {
    gap: 12px;
    align-items: stretch;
  }

  .gender-option {
    padding: 12px;
    border: 1px solid #e6e3ec;
    border-radius: 12px;
  }
}

@media (max-width: 767px) {
  .gender-step {
    padding: 0 20px 20px;
    gap: 40px;
  }

  .gender-selection-content {
    gap: 40px;
    padding-top: 30px;
  }

  .title-section {
    align-items: flex-start;
    text-align: left;
    gap: 8px;
  }

  .step-title {
    font-size: 32px;
    line-height: 1.09;
    font-weight: 700;
  }

  .step-subtitle {
    font-size: 15px;
    line-height: 1.3;
  }

  .gender-options {
    gap: 12px;
    align-items: stretch;
  }

  .gender-option {
    padding: 12px;
    border: 1px solid #e6e3ec;
    border-radius: 12px;
  }
}

@media (min-width: 1024px) and (max-width: 1439px) {
  .gender-step {
    padding: 40px;
    gap: 40px;
  }

  .gender-selection-content {
    gap: 40px;
  }

  .title-section {
    text-align: center;
    gap: 8px;
    align-items: center;
  }

  .step-title {
    font-size: 48px;
    line-height: 1.2;
    font-weight: 700;
  }

  .step-subtitle {
    font-size: 20px;
    line-height: 1.3;
  }

  .gender-options {
    gap: 12px;
    align-items: stretch;
  }

  .gender-option {
    padding: 12px;
    border: 1px solid #e6e3ec;
    border-radius: 12px;
    height: 80px;
  }
}

@media (min-width: 1440px) and (max-width: 1919px) {
  .gender-step {
    padding: 40px;
    gap: 40px;
  }

  .gender-selection-content {
    gap: 40px;
  }

  .title-section {
    text-align: center;
    gap: 8px;
    align-items: center;
  }

  .step-title {
    font-size: 48px;
    line-height: 1.2;
    font-weight: 700;
  }

  .step-subtitle {
    font-size: 20px;
    line-height: 1.3;
  }

  .gender-options {
    gap: 12px;
    align-items: stretch;
  }

  .gender-option {
    padding: 12px;
    border: 1px solid #e6e3ec;
    height: 80px;
    border-radius: 12px;
  }
}

@media (min-width: 1920px) {
  .gender-step {
    padding: 40px 120px;
    gap: 40px;
  }

  .gender-selection-content {
    gap: 40px;
  }

  .title-section {
    text-align: center;
    gap: 8px;
  }

  .step-title {
    font-size: 48px;
    line-height: 1.2;
    font-weight: 700;
  }

  .step-subtitle {
    font-size: 20px;
    line-height: 1.3;
  }

  .gender-options {
    gap: 12px;
    align-items: stretch;
  }

  .gender-option {
    padding: 12px;
    border: 1px solid #e6e3ec;
    border-radius: 12px;
  }
}


</style>

